import { useOrganisation, useSpv } from "@inrange/building-manager-api-client";
import {
  BreadCrumb,
  ErrorSidebar,
  Loaded,
  Loading,
  SidebarWithHeader,
  SimpleButton,
  SpvViewHeader,
} from "@inrange/theme-components";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import * as Sentry from "@sentry/react";
import React, { useContext } from "react";
import { useLocation, useParams } from "react-router-dom";
import { UserContext } from "../auth/UserContext";
import { countSitesByOwnershipAndOperationalStatus } from "./dashboard/aggregationCalculations";
import SpvDocuments from "./SpvViewTabs/SpvDocuments";
import SpvSummary from "./SpvViewTabs/SpvSummary";

const SpvView: React.FC = () => {
  const { logout } = useKindeAuth();
  const doLogout = () => {
    if (import.meta.env.PROD) {
      Sentry.setUser(null);
    }
    logout();
  };

  const location = useLocation();
  const { orgId, spvId, currentTab } = useParams<{
    orgId: string;
    spvId: string;
    currentTab?: string;
  }>();
  const { user, onOrgSwitch } = useContext(UserContext);
  const { fetchOrganisation } = useOrganisation(orgId, orgId);
  const { fetchSpv } = useSpv(orgId, spvId, orgId);

  const organisation = fetchOrganisation.data?.organisation;
  const organisationSiteListSites = fetchOrganisation.data?.sites;
  const spvPerformance = fetchSpv.data?.spvPerformance;

  if (fetchOrganisation.isError) {
    return (
      <ErrorSidebar onClickLogout={doLogout}>
        <Loaded>Organisation not found</Loaded>
      </ErrorSidebar>
    );
  }

  if (fetchSpv.isError) {
    return (
      <ErrorSidebar onClickLogout={doLogout}>
        <Loaded>
          SPV not found
          <SimpleButton
            fontWeight="600"
            label="Go to dashboard"
            to={`/org/${orgId}/dashboard`}
          />
        </Loaded>
      </ErrorSidebar>
    );
  }

  if (
    fetchOrganisation.isLoading ||
    !organisation ||
    !organisationSiteListSites ||
    fetchSpv.isLoading ||
    !spvPerformance
  ) {
    return <Loading label="Loading SPV data..." />;
  }

  const currentUrl = location.pathname;
  const rootPath = `/org/${orgId}/`;

  const siteCounts = countSitesByOwnershipAndOperationalStatus(
    organisationSiteListSites,
    organisation.siteOwnerships
  );

  const orgName = organisation?.name;
  const spvName = spvPerformance?.spvName;
  document.title = orgName
    ? `InRange - ${orgName} - ${spvName}`
    : document.title;

  const crumbs: BreadCrumb[] = [
    {
      label: `${organisation.name} Portfolio`,
      url: `${rootPath}dashboard`,
    },
    { label: spvName },
  ];

  const kindeOrgCode = user!.kinde_org_code;
  const kindeOrgs = user!.kinde_orgs;
  const orgType = organisation?.orgType;

  return (
    <SidebarWithHeader
      crumbs={crumbs}
      currentUrl={currentUrl}
      userEmail={user!.email}
      rootPath={rootPath}
      orgType={orgType!}
      siteCounts={siteCounts}
      onClickLogout={doLogout}
      onOrgSwitch={onOrgSwitch!}
      userOrgs={kindeOrgs}
      currentKindeOrg={kindeOrgCode}
      scoreOrgContext="no-score-details"
    >
      <SpvViewHeader
        spvId={spvId!}
        currentUrl={currentUrl}
        rootPath={rootPath}
      />
      {!currentTab && <SpvSummary spvPerformance={spvPerformance} />}
      {currentTab === "documents" && <SpvDocuments />}
    </SidebarWithHeader>
  );
};

export default SpvView;
